import type { StructureSetId } from '~/domain/changes/ids';
import type { Environment } from '~/engine/data/Environment';
import type { DoseMatrix } from '~/domain/patient/doseMatrix';
import type { Image3D } from '~/domain/patient/image3d';
import type { RigidRegistration } from '~/domain/patient/rigidRegistration';
import type { StructureSet } from '~/domain/patient/structureset';
import {
    assertValidBlendPatch,
    BlendSettings,
    DEFAULT_BLEND_SETTINGS,
    pairHasPet,
} from '~/domain/viewport/fusionBlend';
import { SeriesInstanceUid, toSeriesInstanceUid } from '~/domain/dicom/uids';

/** One registered dataset. Holds the domain `Image3D`; no GPU residency yet. */
export interface DatasetRegistryEntry {
    readonly seriesUid: SeriesInstanceUid;
    readonly image: Image3D;
    /** Owning patient id (plain domain string; branded to `PatientId` at the ACL). */
    readonly patientId: string;
    /**
     * The active structure set, held only while this dataset's image owns
     * it: at most one entry in the registry carries a set. The environment
     * factory attaches it so every tile's shared Environment renders the
     * ROI contours (cross-sectioned per plane from the one shared SDF).
     *
     * The set is addressed through its image's entry because an
     * Environment holds its set's own image and the SDF grid is that
     * image's grid. This is a stand-in, not the settled model: a set that
     * can be addressed apart from its image, and drawn on a row of another
     * series through a registration, replaces it.
     */
    readonly structureSet?: StructureSet;
    /**
     * The active dose, held only while this entry's structure set is the
     * set the dose references: at most one entry in the registry carries a
     * dose, and it is always the entry that carries the dose's set. Every
     * registry write keeps that so, because the engine draws a dose on
     * its set's image and on no other.
     */
    readonly dose?: DoseMatrix;
    /**
     * The image fused onto this dataset as a secondary (blend) layer, if any.
     * Held on the entry, not only the resident `Environment`, so a fusion
     * survives a tile remount that mints a fresh Environment.
     */
    readonly secondaryImage?: Image3D;
    /** Registration linking the secondary image's frame of reference to this dataset's. */
    readonly registration?: RigidRegistration;
    /**
     * How this dataset's fusion is drawn: the mix, the view mode, the
     * checkerboard tile, the colours. Held on the entry, not only the
     * resident `Environment`, so a tuned blend survives a tile remount
     * that mints a fresh Environment. The defaults until moved.
     */
    readonly blend: BlendSettings;
    // Shown read-only for comparison; never the edit target, and never on the active set's entry.
    readonly reference?: DatasetReference;
}

export interface DatasetReference {
    readonly structureSet?: StructureSet;
    readonly dose?: DoseMatrix;
}

/**
 * Creates (and uploads) the one shared `Environment` for a dataset from its
 * full registry entry, attaching whichever of structure set, dose and fusion
 * the dataset carries so every tile of the dataset renders them.
 */
export type EnvironmentFactory = (entry: DatasetRegistryEntry) => Environment;

/** Handle to the one shared `Environment` for a dataset; refcounted. */
export interface DatasetEnvironmentHandle {
    readonly seriesUid: SeriesInstanceUid;
    readonly environment: Environment;
}

/** One resident dataset whose contour save failed during a registry flush. */
interface ResidentSaveFailure {
    readonly seriesUid: SeriesInstanceUid;
    readonly error: unknown;
}

/**
 * Aggregate thrown by
 * {@link DatasetEnvironmentRegistry.saveAllResidentChanges}: every failed
 * dataset is listed, none is silently dropped. The message carries the
 * count only; the series UIDs stay on `failures`, off any log line.
 */
export class ResidentSaveError extends Error {
    public constructor(
        public readonly failures: ReadonlyArray<ResidentSaveFailure>
    ) {
        super(
            `Failed to save contour edits for ${failures.length} dataset(s).`
        );
        this.name = 'ResidentSaveError';
    }
}

interface ResidentEnvironment {
    readonly environment: Environment;
    refCount: number;
}

/**
 * The blend a pair of these modalities may hold: the record as it is,
 * unless it names a checkerboard for a pair with a PET image, which the
 * renderer draws only over a blend.
 */
function blendForPair(
    blend: BlendSettings,
    modalities: ReadonlyArray<string>
): BlendSettings {
    return blend.mode === 'checkerboard' && pairHasPet(modalities)
        ? { ...blend, mode: 'blend' }
        : blend;
}

/**
 * Keyed, patient-scoped resolver over the imported `Image3D`s — the N-volume
 * source of truth the viewport grid binds against, and the owner of GPU
 * residency for every dataset acquired from it and not yet released, shown
 * in a tile or not.
 *
 * Metadata (register / unregister / listEntries / entryFor) answers "which
 * datasets exist" from `Image3D` getters alone. GPU residency (acquire / release) mints exactly
 * one shared `Environment` per distinct series on first acquire and disposes it
 * on last release — this is what makes "same dataset in two tiles/rows => one
 * shared image + SDF" true. `acquire` is single-flight because environment
 * creation + upload is synchronous, so there is no window for a reentrant
 * second mint.
 */
export class DatasetEnvironmentRegistry {
    private readonly entries = new Map<
        SeriesInstanceUid,
        DatasetRegistryEntry
    >();
    private readonly residents = new Map<
        SeriesInstanceUid,
        ResidentEnvironment
    >();
    /**
     * Lost-edit marks of residents disposed on their last release. The
     * Environment that held a mark is gone, so the registry keeps it until
     * the set is activated again or the registry is cleared.
     */
    private readonly releasedLostEditIds = new Set<StructureSetId>();
    private environmentFactory: EnvironmentFactory | undefined;

    private readonly lifecycleListeners = new Set<() => void>();
    private readonly fusionListeners = new Map<
        SeriesInstanceUid,
        Set<() => void>
    >();

    /**
     * Register an imported volume, keyed by series UID. Re-registering a series
     * replaces its entry with the fresh image so a re-import never renders
     * stale data; a structure set, dose, or fusion already attached is kept so
     * re-importing an image alone does not drop them. They are kept only for
     * the same patient: a series of another patient under the same UID starts
     * with none, so no other patient's data is drawn with it. The same series
     * never yields two entries. Lifecycle subscribers are notified.
     */
    public register(image: Image3D, patientId: string): void {
        const seriesUid = toSeriesInstanceUid(image.seriesUid);
        const previous = this.entries.get(seriesUid);
        const existing =
            previous?.patientId === patientId ? previous : undefined;
        this.entries.set(seriesUid, {
            seriesUid,
            image,
            patientId,
            structureSet: existing?.structureSet,
            dose: existing?.dose,
            secondaryImage: existing?.secondaryImage,
            registration: existing?.registration,
            blend: existing?.blend ?? DEFAULT_BLEND_SETTINGS,
        });
        this.notifyLifecycle();
    }

    /**
     * Remove a dataset whose image the patient no longer holds. Its entry
     * goes, and the structure set and dose it carried go with it. Every
     * dataset that fuses this series as its secondary image loses that
     * fusion. A resident `Environment` of the dataset stays until its last
     * reference is released, so the holder of a tile that still shows it
     * tears it down, never this call. Lifecycle subscribers are notified.
     * An unknown series is a no-op.
     */
    public unregister(seriesInstanceUid: string): void {
        const entry = this.entryFor(seriesInstanceUid);
        if (!entry) {
            return;
        }
        this.entries.delete(entry.seriesUid);
        const fusedOnto = [...this.entries.values()].filter(
            (other) => other.secondaryImage?.seriesUid === entry.seriesUid
        );
        for (const other of fusedOnto) {
            this.clearFusion(other.seriesUid);
        }
        this.notifyLifecycle();
    }

    /**
     * Make `structureSet` the one structure set the grid draws: attached to
     * the entry of its image and detached from every other entry, each
     * change pushed live to that dataset's resident `Environment` so open
     * tiles refresh. `undefined` detaches it everywhere. A set whose image
     * is not registered leaves every entry empty and returns false. An
     * entry that already holds what it should is left untouched, so
     * re-activating the current set repaints nothing.
     *
     * Detaching a set frees the SDF textures its unsaved grid edits live
     * in; those edits reach the domain only through the Environment's
     * backstop save, so a caller that can flush explicitly must flush
     * first.
     *
     * Activating a set whose image is registered drops the lost-edit mark
     * kept from a released resident: every Environment that shows the set
     * from here on is built from the domain, so the viewer shows what a
     * save would persist.
     */
    public setActiveStructureSet(
        structureSet: StructureSet | undefined
    ): boolean {
        const imageSeriesUid = structureSet?.image?.seriesUid;
        const owner =
            imageSeriesUid === undefined
                ? undefined
                : this.entryFor(imageSeriesUid);
        if (owner?.reference && structureSet !== undefined) {
            this.entries.set(owner.seriesUid, {
                ...owner,
                reference: undefined,
            });
            this.residents.get(owner.seriesUid)?.environment.setDose(undefined);
        }
        for (const [seriesUid, entry] of this.entries) {
            const next =
                seriesUid === owner?.seriesUid ? structureSet : undefined;
            if (entry.structureSet === next) {
                continue;
            }
            // A dose is drawn on its set's image; a set that leaves the
            // entry takes its dose with it, and the dose comes off the
            // resident first because an Environment keeps its dose when
            // its set is merely removed.
            this.entries.set(seriesUid, {
                ...entry,
                structureSet: next,
                dose: undefined,
            });
            const environment = this.residents.get(seriesUid)?.environment;
            if (entry.dose !== undefined) {
                environment?.setDose(undefined);
            }
            environment?.setStructureSet(next);
        }
        if (owner !== undefined && structureSet !== undefined) {
            this.releasedLostEditIds.delete(structureSet.id);
        }
        return owner !== undefined;
    }

    /**
     * Make `dose` the one dose the grid draws: attached to the entry that
     * carries the structure set the dose references and detached from every
     * other entry, each change pushed live to that dataset's resident
     * `Environment` so open tiles refresh. `undefined` detaches it
     * everywhere. A dose whose set no entry carries, or that references no
     * set, leaves every entry without a dose and returns false: the grid
     * never draws a dose on an image other than its set's. An entry that
     * already holds what it should is left untouched, so re-activating the
     * current dose repaints nothing.
     */
    public setActiveDose(dose: DoseMatrix | undefined): boolean {
        const doseStructureSet = dose?.structureSet ?? undefined;
        const owner =
            doseStructureSet === undefined
                ? undefined
                : this.entryCarrying(doseStructureSet);
        for (const [seriesUid, entry] of this.entries) {
            const next = entry === owner ? dose : undefined;
            if (entry.dose === next) {
                continue;
            }
            this.entries.set(seriesUid, { ...entry, dose: next });
            this.residents.get(seriesUid)?.environment.setDose(next);
        }
        return owner !== undefined;
    }

    // At most one entry shows a reference; refused for an unregistered entry, the active set's entry, or a dose of another set.
    public setReference(
        seriesUid: string | undefined,
        reference?: DatasetReference
    ): boolean {
        const owner =
            seriesUid === undefined ? undefined : this.entryFor(seriesUid);
        if (
            (seriesUid !== undefined && (!owner || owner.structureSet)) ||
            (reference?.dose !== undefined &&
                reference.dose.structureSet !== reference.structureSet)
        ) {
            return false;
        }
        for (const [uid, entry] of this.entries) {
            const next = entry === owner ? reference : undefined;
            if (
                entry.reference?.structureSet === next?.structureSet &&
                entry.reference?.dose === next?.dose
            ) {
                continue;
            }
            this.entries.set(uid, { ...entry, reference: next });
            const environment = this.residents.get(uid)?.environment;
            environment?.setDose(undefined);
            environment?.setStructureSet(next?.structureSet);
            if (next?.dose) {
                environment?.setDose(next.dose);
            }
        }
        return true;
    }

    /** The entry whose structure set is `structureSet` itself, if any. */
    private entryCarrying(
        structureSet: StructureSet
    ): DatasetRegistryEntry | undefined {
        return this.findEntry((entry) => entry.structureSet === structureSet);
    }

    /** The first entry, in registration order, that satisfies `predicate`. */
    private findEntry(
        predicate: (entry: DatasetRegistryEntry) => boolean
    ): DatasetRegistryEntry | undefined {
        for (const entry of this.entries.values()) {
            if (predicate(entry)) {
                return entry;
            }
        }
        return undefined;
    }

    /**
     * The dataset entry whose structure set carries this DICOM UID. Undefined
     * when no registered dataset holds it.
     */
    public entryForStructureSet(
        structureSetUid: string
    ): DatasetRegistryEntry | undefined {
        return this.findEntry(
            (entry) => entry.structureSet?.dicomUid === structureSetUid
        );
    }

    /**
     * Attach a secondary (fusion) image and its registration to a dataset
     * entry, matched by the primary series UID. The tuned blend stays,
     * except that a pair with a PET image is never drawn as a
     * checkerboard: such a pair falls back to the blend. If the dataset's
     * shared `Environment` is already resident, the fusion is pushed to it
     * live so an already-open tile blends immediately. A missing entry is
     * a no-op: there is nothing to fuse onto.
     */
    public registerFusion(
        primarySeriesUid: string,
        secondaryImage: Image3D,
        registration?: RigidRegistration
    ): void {
        const entry = this.entryFor(primarySeriesUid);
        if (!entry) {
            return;
        }
        const blend = blendForPair(entry.blend, [
            entry.image.seriesMetadata.modality,
            secondaryImage.seriesMetadata.modality,
        ]);
        this.entries.set(entry.seriesUid, {
            ...entry,
            secondaryImage,
            registration,
            blend,
        });
        const resident = this.residents.get(entry.seriesUid);
        if (resident) {
            resident.environment.fuseSecondaryImage(
                secondaryImage,
                registration
            );
            resident.environment.applyBlendSettings(blend);
        }
        this.notifyLifecycle();
        this.notifyFusion(entry.seriesUid);
    }

    /**
     * Remove a dataset's fusion, matched by the primary series UID, and
     * reset its blend to the defaults so a later re-fuse starts from the
     * even, untinted mix rather than wherever the controls were last
     * left. If the dataset's shared `Environment` is already resident,
     * the fusion is cleared from it live and it forgets the tuned blend
     * too. A missing entry is a no-op; one with no fusion still has its
     * blend reset.
     */
    public clearFusion(primarySeriesUid: string): void {
        const entry = this.entryFor(primarySeriesUid);
        if (!entry) {
            return;
        }
        this.entries.set(entry.seriesUid, {
            ...entry,
            secondaryImage: undefined,
            registration: undefined,
            blend: DEFAULT_BLEND_SETTINGS,
        });
        const resident = this.residents.get(entry.seriesUid);
        if (resident) {
            resident.environment.clearSecondaryFusion();
            resident.environment.applyBlendSettings(DEFAULT_BLEND_SETTINGS);
        }
        this.notifyLifecycle();
        this.notifyFusion(entry.seriesUid);
    }

    /**
     * Remove the fusion of every dataset fused through `registration`, in
     * either direction. For a registration the patient no longer holds, so
     * no row keeps displaying, or editing, a link that no longer exists.
     */
    public clearFusionsThrough(registration: RigidRegistration): void {
        for (const entry of this.entries.values()) {
            if (entry.registration === registration) {
                this.clearFusion(entry.seriesUid);
            }
        }
    }

    /**
     * Change part of how a dataset's fusion is drawn, matched by the
     * primary series UID: only the fields in `patch` move. If the
     * dataset's shared `Environment` is already resident, the patch is
     * pushed to it live so an already-open tile reflects the moved
     * control immediately. A missing entry is a no-op: there is nothing
     * to blend. A patch naming a value no control can produce throws
     * before anything is written.
     */
    public setBlend(
        primarySeriesUid: string,
        patch: Partial<BlendSettings>
    ): void {
        const entry = this.entryFor(primarySeriesUid);
        if (!entry) {
            return;
        }
        assertValidBlendPatch(patch);
        this.entries.set(entry.seriesUid, {
            ...entry,
            blend: { ...entry.blend, ...patch },
        });
        const resident = this.residents.get(entry.seriesUid);
        if (resident) {
            resident.environment.applyBlendSettings(patch);
        }
        this.notifyFusion(entry.seriesUid);
    }

    public listEntries(): ReadonlyArray<DatasetRegistryEntry> {
        return [...this.entries.values()];
    }

    public entryFor(
        seriesInstanceUid: string
    ): DatasetRegistryEntry | undefined {
        return this.entries.get(seriesInstanceUid.trim() as SeriesInstanceUid);
    }

    /**
     * Wire the engine-side factory that mints + uploads an `Environment`. Set
     * once when the engine is available; without it the registry stays
     * metadata-only and {@link acquire} fails loud rather than touching the GPU.
     */
    public setEnvironmentFactory(factory: EnvironmentFactory): void {
        this.environmentFactory = factory;
    }

    /**
     * Acquire the one shared `Environment` for a dataset, minting + uploading it
     * on first acquire and bumping a reference count on subsequent acquires of
     * the same series (no second upload). Balance every acquire with a
     * {@link release}.
     */
    public acquire(seriesInstanceUid: string): DatasetEnvironmentHandle {
        const entry = this.entryFor(seriesInstanceUid);
        if (!entry) {
            throw new Error(
                'Cannot acquire an unregistered dataset; register the Image3D first.'
            );
        }
        if (!this.environmentFactory) {
            throw new Error(
                'DatasetEnvironmentRegistry has no environment factory; ' +
                    'GPU residency is not wired.'
            );
        }
        let resident = this.residents.get(entry.seriesUid);
        if (!resident) {
            resident = {
                environment: this.environmentFactory(entry),
                refCount: 0,
            };
            this.residents.set(entry.seriesUid, resident);
        }
        resident.refCount += 1;
        this.notifyLifecycle();
        return {
            seriesUid: entry.seriesUid,
            environment: resident.environment,
        };
    }

    /**
     * Subscribe to dataset registration changes, to acquire/release
     * lifecycle changes and to fusion writes. Environments are disposed at
     * zero references and re-minted on re-acquire, so anything holding
     * Environment references (e.g. the cursor bridge) must refresh on these
     * notifications, not only on layout changes. A fuse or clear changes
     * what a resident Environment holds, so resolvers of a dataset's fused
     * pair refresh on it too. A register or unregister changes which
     * datasets exist, so lists of bindable datasets refresh on it.
     */
    public subscribeLifecycle(listener: () => void): () => void {
        this.lifecycleListeners.add(listener);
        return () => {
            this.lifecycleListeners.delete(listener);
        };
    }

    private notifyLifecycle(): void {
        this.lifecycleListeners.forEach((listener) => listener());
    }

    /**
     * Subscribe to changes of one dataset's fusion: a fuse, a clear, or a
     * moved blend control, matched by the primary series UID and whoever
     * wrote it. Narrower than lifecycle, which also fires on every
     * acquire/release and not on a blend move.
     */
    public subscribeFusion(
        primarySeriesUid: string,
        listener: () => void
    ): () => void {
        const key = primarySeriesUid.trim() as SeriesInstanceUid;
        const listeners = this.fusionListeners.get(key) ?? new Set();
        this.fusionListeners.set(key, listeners);
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
            // Only the set still registered is dropped: a stale unsubscribe
            // must not remove a later subscriber's set.
            if (
                listeners.size === 0 &&
                this.fusionListeners.get(key) === listeners
            ) {
                this.fusionListeners.delete(key);
            }
        };
    }

    private notifyFusion(primarySeriesUid: SeriesInstanceUid): void {
        this.fusionListeners
            .get(primarySeriesUid)
            ?.forEach((listener) => listener());
    }

    /**
     * Release one reference to a dataset's `Environment`. At zero references the
     * `Environment` is disposed (its image/SDF/dose textures freed), and its
     * lost-edit marks, including one its backstop save records during the
     * disposal, are kept by the registry.
     */
    public release(seriesInstanceUid: string): void {
        const key = seriesInstanceUid.trim() as SeriesInstanceUid;
        const resident = this.residents.get(key);
        if (!resident) {
            return;
        }
        resident.refCount -= 1;
        if (resident.refCount <= 0) {
            resident.environment.setImage(undefined);
            for (const id of resident.environment.lostEditStructureSetIds()) {
                this.releasedLostEditIds.add(id);
            }
            this.residents.delete(key);
        }
        this.notifyLifecycle();
    }

    /**
     * Persist every resident `Environment`'s contour edits to the domain
     * model (`Environment.saveChanges` ->
     * `StructureSetObject.saveChanges`). Grid edits live only in these
     * Environments' SDF textures until saved, so every boundary that
     * tears tiles down or snapshots domain contours (export, upload,
     * copy, patient unload) must flush through here first.
     *
     * Safe to call repeatedly: `StructureSetObject.saveChanges` writes
     * only ROIs flagged `hasBeenModified`, and reading `roi.sequence`
     * during that write clears the flag, so a second flush is a cheap
     * no-op.
     *
     * Attempts every resident even after one fails, then throws a
     * {@link ResidentSaveError} aggregating all failures — a failed
     * clinical write is never swallowed.
     *
     * Returns the union of every resident's lost-edit marks and of those
     * kept from released residents: the structure sets whose edits were
     * lost to an earlier failed switch-away flush (see
     * `Environment.saveChanges`). Their domain copy is stale and must not
     * be persisted or exported as current.
     */
    public saveAllResidentChanges(): ReadonlySet<StructureSetId> {
        const failures: ResidentSaveFailure[] = [];
        const lostEditIds = new Set(this.releasedLostEditIds);
        for (const [seriesUid, resident] of this.residents) {
            try {
                for (const id of resident.environment.saveChanges()) {
                    lostEditIds.add(id);
                }
            } catch (error) {
                failures.push({ seriesUid, error });
            }
        }
        if (failures.length > 0) {
            throw new ResidentSaveError(failures);
        }
        return lostEditIds;
    }

    /** The live shared `Environment` for a series, if one is currently acquired. */
    public environmentFor(seriesInstanceUid: string): Environment | undefined {
        return this.residents.get(seriesInstanceUid.trim() as SeriesInstanceUid)
            ?.environment;
    }

    /** Every live shared `Environment`, one per acquired series. */
    public residentEnvironments(): ReadonlyArray<Environment> {
        return [...this.residents.values()].map(
            (resident) => resident.environment
        );
    }

    /**
     * Drop every entry and dispose every resident `Environment` (its
     * textures freed) regardless of outstanding refcounts — the
     * whole-registry reset for patient unload. Callers must flush via
     * {@link saveAllResidentChanges} first; the dispose path only
     * backstops unsaved edits with a loud assertion. Every kept lost-edit
     * mark is dropped with the patient it belongs to.
     */
    public clear(): void {
        const hadResidents = this.residents.size > 0;
        for (const resident of this.residents.values()) {
            resident.environment.setImage(undefined);
        }
        this.residents.clear();
        this.entries.clear();
        this.releasedLostEditIds.clear();
        if (hadResidents) {
            this.notifyLifecycle();
        }
    }
}
